/* eslint-disable max-lines-per-function, complexity, max-lines */
/* Cohesive multi-field booking modal — mirrors PartnerModal pattern. */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Mic, Loader2, CalendarCheck } from "lucide-react";
import { submitSpeakingInquiry } from "@/lib/api";
import { toast } from "sonner";

const ENGAGEMENT_TYPES = [
    "Keynote Address",
    "Conference",
    "Leadership Summit",
    "Professional Development Training",
    "Educational Workshop",
    "Corporate Event",
    "Community Event",
    "Panel Discussion",
    "Podcast Interview",
    "Media Appearance",
    "Youth Leadership Program",
    "Nonprofit Event",
    "Business / Entrepreneurship Forum",
];

const initialForm = {
    full_name: "",
    organization: "",
    email: "",
    phone: "",
    event_type: "",
    event_date: "",
    audience_size: "",
    location: "",
    details: "",
};

export const CEOBookingModal = ({ open, onClose }) => {
    const [data, setData] = useState(initialForm);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (e) =>
        setData({ ...data, [e.target.name]: e.target.value });

    const close = () => {
        if (sending) return;
        onClose();
        // small delay so the closing animation runs before reset
        setTimeout(() => {
            setDone(false);
            setData(initialForm);
        }, 300);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await submitSpeakingInquiry(data);
            setDone(true);
            toast.success("Inquiry sent — the team will reach out soon.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Couldn't submit — please email EDUtainmentpros@gmail.com directly.",
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center bg-black/85 backdrop-blur-sm overflow-y-auto p-4 sm:p-8"
                    onClick={close}
                    data-testid="ceo-booking-modal"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.98 }}
                        transition={{ duration: 0.3 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-2xl bg-[#0a0a0a] border border-[#D4AF37]/30 my-auto"
                    >
                        <button
                            onClick={close}
                            data-testid="ceo-booking-close"
                            className="absolute top-3 right-3 h-9 w-9 grid place-items-center text-white/60 hover:text-[#D4AF37] transition-colors z-10"
                            aria-label="Close"
                        >
                            <X size={20} />
                        </button>

                        {done ? (
                            <div className="px-8 py-12 text-center">
                                <div className="inline-flex items-center justify-center h-14 w-14 border border-[#D4AF37] mb-4">
                                    <CalendarCheck size={24} className="text-[#D4AF37]" />
                                </div>
                                <div className="font-display text-2xl uppercase tracking-tight text-white mb-2">
                                    Inquiry Received
                                </div>
                                <p className="text-white/70 max-w-md mx-auto">
                                    Thanks for reaching out. The team will
                                    follow up shortly with availability and
                                    next steps.
                                </p>
                                <button
                                    type="button"
                                    onClick={close}
                                    data-testid="ceo-booking-done-close"
                                    className="mt-6 px-5 py-2.5 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black text-[11px] uppercase tracking-[0.3em] font-bold transition-colors"
                                >
                                    Done
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} className="p-6 sm:p-8">
                                <div className="flex items-center gap-3 mb-2">
                                    <Mic size={14} className="text-[#D4AF37]" />
                                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                                        Request Availability
                                    </span>
                                </div>
                                <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-[1] text-white">
                                    Book{" "}
                                    <span className="gold-text-gradient">
                                        Hayón
                                    </span>
                                </h3>
                                <p className="mt-3 text-white/65 text-sm">
                                    Share a few details about your event and
                                    the team will reach out with availability.
                                </p>

                                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                                    <Field
                                        name="full_name"
                                        value={data.full_name}
                                        onChange={onChange}
                                        placeholder="Full Name *"
                                        required
                                    />
                                    <Field
                                        name="organization"
                                        value={data.organization}
                                        onChange={onChange}
                                        placeholder="Organization *"
                                        required
                                    />
                                    <Field
                                        type="email"
                                        name="email"
                                        value={data.email}
                                        onChange={onChange}
                                        placeholder="Email *"
                                        required
                                    />
                                    <Field
                                        type="tel"
                                        name="phone"
                                        value={data.phone}
                                        onChange={onChange}
                                        placeholder="Phone Number *"
                                        required
                                    />
                                    <select
                                        name="event_type"
                                        value={data.event_type}
                                        onChange={onChange}
                                        required
                                        data-testid="ceo-form-event-type"
                                        className="bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white"
                                    >
                                        <option value="">Event Type *</option>
                                        {ENGAGEMENT_TYPES.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                    <label className="flex flex-col gap-1.5">
                                        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45 ml-1">
                                            Event Date *
                                        </span>
                                        <input
                                            type="date"
                                            name="event_date"
                                            value={data.event_date}
                                            onChange={onChange}
                                            required
                                            data-testid="ceo-form-event_date"
                                            className="bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-[10px] text-sm text-white"
                                        />
                                    </label>
                                    <Field
                                        name="audience_size"
                                        value={data.audience_size}
                                        onChange={onChange}
                                        placeholder="Audience Size (optional)"
                                    />
                                    <Field
                                        name="location"
                                        value={data.location}
                                        onChange={onChange}
                                        placeholder="Location *"
                                        required
                                    />
                                </div>
                                <textarea
                                    name="details"
                                    value={data.details}
                                    onChange={onChange}
                                    rows={4}
                                    required
                                    placeholder="Additional Details — topic, audience, format... *"
                                    data-testid="ceo-form-details"
                                    className="mt-3 w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white placeholder:text-white/35 resize-none"
                                />
                                <div className="mt-5 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={close}
                                        disabled={sending}
                                        className="px-5 py-3 border border-white/20 text-white/70 hover:border-white hover:text-white text-[12px] uppercase tracking-[0.3em] font-bold transition-colors disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        data-testid="ceo-form-submit"
                                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black text-[12px] uppercase tracking-[0.3em] font-bold hover:bg-[#E5C158] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {sending ? (
                                            <>
                                                <Loader2 size={14} className="animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send size={14} />
                                                Request Availability
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

function Field({ name, value, onChange, placeholder, type = "text", required }) {
    return (
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            data-testid={`ceo-form-${name}`}
            className="bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white placeholder:text-white/35"
        />
    );
}
